/**
 * Watrly Checkbox Component
 * =========================
 * مكون صندوق التحديد (شبيه بشعار وترلي - قطرة ماء)
 */

import React, { forwardRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Checkbox.css';

const Checkbox = forwardRef(({
  checked = false,
  onChange,
  label,
  description,
  disabled = false,
  error,
  size = 'md',
  variant = 'water-drop', // water-drop | square | circle
  className = '',
  id,
  name,
  value,
  indeterminate = false,
  ...props
}, ref) => {
  const { isRTL } = useTheme();

  const inputId = id || name || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  const containerClasses = [
    'watrly-checkbox',
    `watrly-checkbox--${size}`,
    `watrly-checkbox--${variant}`,
    disabled && 'watrly-checkbox--disabled',
    error && 'watrly-checkbox--error',
    checked && 'watrly-checkbox--checked',
    indeterminate && 'watrly-checkbox--indeterminate',
    className,
  ].filter(Boolean).join(' ');

  const handleChange = (e) => {
    if (!disabled && onChange) {
      onChange(e);
    }
  };

  return (
    <div className={containerClasses} dir={isRTL ? 'rtl' : 'ltr'}>
      <label className="watrly-checkbox__container">
        <input
          ref={ref}
          type="checkbox"
          id={inputId}
          name={name}
          value={value}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="watrly-checkbox__input"
          aria-describedby={description ? `${inputId}-desc` : undefined}
          {...props}
        />

        <span className="watrly-checkbox__box">
          {variant === 'water-drop' ? (
            // شكل قطرة الماء
            <svg
              className="watrly-checkbox__icon"
              viewBox="0 0 24 24"
              fill="none"
              width="24"
              height="24"
            >
              {/* قطرة الماء */}
              <path
                className="watrly-checkbox__drop"
                d="M12 2C12 2 6 10 6 14C6 17.3137 8.68629 20 12 20C15.3137 20 18 17.3137 18 14C18 10 12 2 12 2Z"
              />
              {/* علامة الصح */}
              <path
                className="watrly-checkbox__check"
                d="M8.5 14L10.5 16L15.5 11"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* علامة ناقص للـ indeterminate */}
              <path
                className="watrly-checkbox__minus"
                d="M8 14H16"
                strokeLinecap="round"
              />
            </svg>
          ) : variant === 'circle' ? (
            // شكل دائري
            <svg
              className="watrly-checkbox__icon"
              viewBox="0 0 24 24"
              fill="none"
              width="24"
              height="24"
            >
              <circle
                className="watrly-checkbox__circle-bg"
                cx="12"
                cy="12"
                r="10"
              />
              <path
                className="watrly-checkbox__check"
                d="M8 12L10.5 14.5L16 9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                className="watrly-checkbox__minus"
                d="M8 12H16"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            // شكل مربع
            <svg
              className="watrly-checkbox__icon"
              viewBox="0 0 24 24"
              fill="none"
              width="24"
              height="24"
            >
              <rect
                className="watrly-checkbox__rect-bg"
                x="3"
                y="3"
                width="18"
                height="18"
                rx="4"
              />
              <path
                className="watrly-checkbox__check"
                d="M8 12L10.5 14.5L16 9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                className="watrly-checkbox__minus"
                d="M8 12H16"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>

        {(label || description) && (
          <span className="watrly-checkbox__content">
            {label && <span className="watrly-checkbox__label">{label}</span>}
            {description && (
              <span id={`${inputId}-desc`} className="watrly-checkbox__description">
                {description}
              </span>
            )}
          </span>
        )}
      </label>

      {error && (
        <p className="watrly-checkbox__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
